import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AchatService } from '../../../../services/achat/achat.service';
import { HeaderService } from '../../../../services/header/header.service';
import { ExportService } from '../../shared/export.service';
import { ToastrService } from 'ngx-toastr';
import { DataService } from '../../../../services/commande-client/data.service';
import { Subscription } from 'rxjs';
import { isAvoirTableRow, isFactureTableRowExcludingAvoirs } from '../../../../services/document/document-avoir-row.util';

@Component({
  selector: 'app-facture-fournisseur',
  templateUrl: './facture-fournisseur.component.html',
  styleUrls: ['./facture-fournisseur.component.scss']
})
export class FactureFournisseurComponent implements OnInit, OnDestroy {
    currentStatus: string = 'tous';
    statuses: string[] = ['tous', 'brouillon', 'paye', 'nonPaye', 'annule', 'partPaye'];
    currentUser: any = null;
    userId: number | null = null;
    stationId: number | null = null;
    private dataSub?: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private achatService: AchatService,
        private dataService: DataService,
        private headerService: HeaderService,
        private exportService: ExportService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef
    ) {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        this.userId = this.currentUser?.id || null;
        this.stationId = this.currentUser?.stations?.[0]?.id || null;
    }

    ngOnInit(): void {
        this.dataSub = this.achatService.factureFournisseurTableData$.subscribe(() => {
            setTimeout(() => this.cdr.detectChanges(), 0);
        });
        console.log('CommandeClientComponent initialized');
        this.headerService.setConfig({
            title: 'Facture fournisseurs',
            buttons: [
                {
                    label: 'Exporter',
                    type: 'dropdown',
                    dropdownItems: [
                        {
                            label: 'PDF',
                            action: () => this.exportService.triggerPDF(),
                            icon: '../../../assets/images/logo/pdf_logo.png'
                        },
                        {
                            label: 'CSV',
                            action: () => this.exportToCSV(),
                            icon: '../../../assets/images/logo/csv_logo.png'
                        }
                    ]
                }
            ]
        });
        // Mettre à jour currentStatus à partir de l'URL
        this.route.firstChild?.url.subscribe(segments => {
            const path = segments.length > 0 ? segments[0].path : 'tous';
            // Mapper 'part-paye' vers 'partPaye' pour la cohérence
            if (path.toLowerCase() === 'part-paye') {
                this.currentStatus = 'partPaye';
            } else {
                this.currentStatus = path.toLowerCase();
            }
            console.log('Current status set to:', this.currentStatus); // Debug
        });
    }

    ngOnDestroy(): void {
        this.dataSub?.unsubscribe();
    }

    filterByStatus(status: string) {
        // Mapper 'partPaye' vers 'part-paye' pour l'URL
        const urlPath = status === 'partPaye' ? 'part-paye' : status.toLowerCase();
        // Conserver la casse originale pour partPaye, sinon utiliser toLowerCase
        this.currentStatus = status === 'partPaye' ? 'partPaye' : status.toLowerCase();
        const mappedStatus = this.mapPathToStatus(status);
        this.achatService.loadByTypeAndStatus('facture-fournisseur', mappedStatus);
        this.router.navigate([`/achat/facture-fournisseur/${urlPath}`]);
    }


    private mapPathToStatus(path: string): string {
        const statusMap: { [key: string]: string } = {
            tous: 'Tous',
            brouillon: 'Brouillon',
            paye: 'Payé',
            non_payé: 'Non payé',
            annule: 'Annulé',
            partpaye: 'Partiellement payé',
            'part-paye': 'Partiellement payé'
        };
        return statusMap[path.toLowerCase()] || 'Tous';
    }
    /** Payé, Brouillon, etc. : total_items (API) − avoirs. Si API pas encore dispo, utilise les données chargées. */
    getStatusCount(status: string): number {
        const data = this.achatService.getFactureFournisseurTableData() || [];
        const arr = Array.isArray(data) ? data : [];
        const factures = arr.filter((d: any) => this.isFactureDocument(d));
        const avoirs = arr.filter((d: any) => this.isAvoirDocument(d));

        if (status === 'Tous') {
            const totalFactureOnly = this.factureStatuses.reduce(
                (sum, s) => sum + this.achatService.getFactureFournisseurTotalItems(s),
                0
            );
            if (totalFactureOnly > 0) return Math.max(0, totalFactureOnly);
            return factures.length;
        }

        const totalFromApi = this.achatService.getFactureFournisseurTotalItems(status);
        const avoirCount = avoirs.filter((i: any) => this.statusMatches(i, status)).length;
        let count = Math.max(0, totalFromApi - avoirCount);
        if (count === 0 && factures.length > 0) {
            const fromData = factures.filter((i: any) => this.statusMatches(i, status)).length;
            count = Math.max(0, fromData - avoirCount);
        }
        return count;
    }

    private statusMatches(item: any, status: string): boolean {
        const s = (item?.status ?? item?.fullData?.statut?.titre ?? '').toString().trim();
        if (status === 'Payé') return s === 'Payé' || s === 'Payée';
        if (status === 'Annulé') return s === 'Annulé' || s === 'Annulée';
        return s === status;
    }

    /** Avoir : flag statut.avoir, titres historiques ou document_original_id. */
    private isAvoirDocument(d: any): boolean {
        return isAvoirTableRow(d);
    }

    private readonly factureStatuses = ['Brouillon', 'Payé', 'Payée', 'Non payé', 'Partiellement payé', 'Annulé', 'Annulée'];

    private isFactureDocument(d: any): boolean {
        return isFactureTableRowExcludingAvoirs(d, this.factureStatuses);
    }

    private exportToCSV(): void {
        const data = this.achatService.getFactureFournisseurTableData() || [];
        const factures = (Array.isArray(data) ? data : []).filter((d: any) => this.isFactureDocument(d));
        const headers = ['Référence', 'Client', 'Date', 'Revenu', 'Matricule', 'Statut'];
        const rows = factures.map(item => [
            `"${(item.reference || '').replace(/"/g, '""')}"`,
            `"${(item.clientName || '').replace(/"/g, '""')}"`,
            `"${(item.date || '').replace(/"/g, '""')}"`,
            `"${(item.revenue || '').replace(/"/g, '""')}"`,
            `"${(item.matricule || '').replace(/"/g, '""')}"`,
            `"${(item.status || '').replace(/"/g, '""')}"`
        ]);
        const csvContent = headers.join(',') + '\n' + rows.map(row => row.join(',')).join('\n');
        this.exportService.downloadCSV(csvContent, 'facture-fournisseur.csv');
    }

}
